import React from "react";
import { Box, Typography } from "@mui/material";
import { Zap, Hash, DollarSign, Cpu, DatabaseZap } from "lucide-react";
import type { TurnMetrics } from "../../chat/hooks/useStreamingTurn";

export interface TurnMetricsFooterProps {
  metrics: TurnMetrics | null;
}

/** Build the metric chip list from a metrics object (any missing field is
 *  omitted). Returns [] when there's nothing to show. */
function metricItems(metrics: TurnMetrics | null) {
  const items: Array<{ key: string; icon: React.ReactNode; text: string }> = [];
  if (!metrics) return items;
  if (typeof metrics.ttft_ms === "number") {
    items.push({ key: "ttft", icon: <Zap size={13} aria-hidden="true" />, text: `${(metrics.ttft_ms / 1000).toFixed(1)}s to first token` });
  }
  if (typeof metrics.input_tokens === "number") {
    items.push({ key: "tokens", icon: <Hash size={13} aria-hidden="true" />, text: `${metrics.input_tokens.toLocaleString()} tokens` });
  }
  // Prompt-cache read: tokens served from Anthropic's cache rather than billed
  // as fresh input. Surfaced as a distinct chip so the user can see the cache
  // is working (and why cost is lower than the raw token count implies). Zero /
  // missing is omitted — no cache hit means nothing worth showing.
  if (typeof metrics.cache_read_input_tokens === "number" && metrics.cache_read_input_tokens > 0) {
    items.push({ key: "cached", icon: <DatabaseZap size={13} aria-hidden="true" />, text: `${metrics.cache_read_input_tokens.toLocaleString()} cached` });
  }
  if (typeof metrics.cost_usd === "number") {
    items.push({ key: "cost", icon: <DollarSign size={13} aria-hidden="true" />, text: `$${metrics.cost_usd.toFixed(3)}` });
  }
  if (metrics.model) {
    items.push({ key: "model", icon: <Cpu size={13} aria-hidden="true" />, text: metrics.model });
  }
  return items;
}

/** True when there are any metric chips to render. Lets callers decide layout
 *  (e.g. whether to draw the actions/metrics footer at all). */
export function hasMetrics(metrics: TurnMetrics | null): boolean {
  return metricItems(metrics).length > 0;
}

/** Just the inline metric chips (no wrapper/divider) — used inside the
 *  assistant bubble's action footer so actions + metrics sit on one row. */
export function TurnMetricsRow({ metrics }: TurnMetricsFooterProps) {
  const items = metricItems(metrics);
  if (items.length === 0) return null;
  return (
    <>
      {items.map((it) => (
        <Box key={it.key} sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.secondary" }}>
          {it.icon}
          <Typography variant="caption">{it.text}</Typography>
        </Box>
      ))}
    </>
  );
}

/** Standalone quiet footer with the turn's cost/latency/token usage. Kept for
 *  the live in-flight turn (rendered by MessageList below the streaming
 *  bubble); persisted history messages render metrics inline via the bubble's
 *  action footer instead. */
export function TurnMetricsFooter({ metrics }: TurnMetricsFooterProps) {
  if (!hasMetrics(metrics)) return null;
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1.5,
        mt: 0.5,
        ml: 5,
        pt: 0.5,
        color: "text.secondary",
      }}
    >
      <TurnMetricsRow metrics={metrics} />
    </Box>
  );
}
